import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { BulkUserType } from '../types';
import { BulkImportFileInput } from './BulkImportFileInput';
import { BulkImportInstructions } from './BulkImportInstructions';

interface BulkImportSetupProps {
  userType: BulkUserType;
  selectedBrandIds: string[];
  brandOptions: Array<{ id: string; name: string }>;
  partnersLoading: boolean;
  partnersError: string | null;
  file: File | null;
  fileError: string;
  setupError: string;
  isProcessing: boolean;
  onUserTypeChange: (_userType: BulkUserType) => void;
  onBrandIdsChange: (_ids: string[]) => void;
  onFileChange: (_file: File | null) => void;
  onPreview: () => void;
}

export function BulkImportSetup({
  userType,
  selectedBrandIds,
  brandOptions,
  partnersLoading,
  partnersError,
  file,
  fileError,
  setupError,
  isProcessing,
  onUserTypeChange,
  onBrandIdsChange,
  onFileChange,
  onPreview,
}: BulkImportSetupProps) {
  const noBrandsAvailable = !partnersLoading && !partnersError && brandOptions.length === 0;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="bulk-user-type" className="text-sm font-medium text-gray-700">
          Tipo de usuário
        </label>
        <Select
          value={userType}
          onValueChange={(value) => onUserTypeChange(value as BulkUserType)}
          disabled={isProcessing}
        >
          <SelectTrigger id="bulk-user-type">
            <SelectValue placeholder="Selecione o tipo de usuário" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="internal">Usuário interno</SelectItem>
            <SelectItem value="partner">Usuário cliente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label htmlFor="bulk-brands" className="text-sm font-medium text-gray-700">
          Marcas
        </label>
        <div id="bulk-brands">
          <MultiSelect
            value={selectedBrandIds}
            onChange={onBrandIdsChange}
            items={brandOptions}
            placeholder="Selecione uma ou mais marcas"
            loading={partnersLoading}
          />
        </div>
        {partnersError && (
          <p className="text-sm text-red-600" role="alert">
            Não foi possível carregar as marcas. Tente novamente.
          </p>
        )}
        {noBrandsAvailable && (
          <p className="text-sm text-muted-foreground">Nenhuma marca disponível no momento.</p>
        )}
      </div>

      <BulkImportInstructions userType={userType} />

      <BulkImportFileInput
        file={file}
        error={fileError}
        disabled={isProcessing}
        loading={isProcessing}
        onChange={onFileChange}
      />

      {setupError && (
        <p className="text-sm text-red-600" role="alert">
          {setupError}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={onPreview} disabled={isProcessing || partnersLoading || noBrandsAvailable}>
          {isProcessing ? 'Processando arquivo...' : 'Processar CSV'}
        </Button>
      </div>
    </div>
  );
}
